import type { FC } from "react"

import styled, { css } from "styled-components"

import { ReactComponent as ExclamationDark } from "../../../assets/svg/exclamation-dark.svg"
import { ReactComponent as Warning } from "../../../assets/svg/warning.svg"
import { useCoordinates, usePortal } from "../utils"

const Wrapper = styled.div`
  height: 100%;
  width: 26px;
  max-width: none;
  cursor: help;
  svg {
    margin-bottom: 1px;
    margin-right: 8px;
  }
`

const Message = styled.div<{ isError: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: fixed;
  z-index: 9000;
  padding: 0.5rem;
  background: var(--white);
  border: 1px solid #ffc1c1;
  box-shadow: 1px 1px 4px 0px #ffc1c1;
  border-radius: 4px;
  span {
    font-family: "Barlow";
    font-style: normal;
    font-weight: 700;
    font-size: 14px;
    line-height: 17px;
    /* or 121% */

    color: ${({ isError }) => (isError ? `var(--red-text)` : `#707070`)};
  }
  ${({ isError }) =>
    isError &&
    css`
      border: 1px solid var(--grey-dark);
      box-shadow: 1px 1px 4px 0px var(--grey-dark);
    `}
`

const MessageCard: FC<{
  children: string
  coords: { left: number; top: number }
  isError: boolean
}> = ({ children, coords }) => {
  return (
    <Message isError style={coords}>
      <span data-testid="tooltip-message">{children}</span>
    </Message>
  )
}

export const Tooltip: FC<{
  message: string | undefined
  inputId: string
  scrollId?: string
  isError: boolean
  showIcon?: boolean
}> = ({ message, isError, inputId, scrollId, showIcon = true }) => {
  const id = `${inputId}-error`
  const { coords } = useCoordinates({
    elementId: id,
    dir: `above`,
    scrollId,
    topMargin: -50,
    leftMargin: -40,
  })
  const { Portal, show, hide } = usePortal({
    elementId: `${id}-portal`,
  })
  if (!message) {
    return null
  }

  return (
    <Wrapper
      id={id}
      data-testid="tooltip"
      aria-description={message}
      onMouseEnter={() => show()}
      onMouseLeave={() => hide()}
    >
      {isError && showIcon && <Warning style={{ height: 15, width: 18 }} />}
      {!isError && showIcon && (
        <ExclamationDark style={{ height: 13, width: 13 }} />
      )}
      <Portal>
        <MessageCard isError coords={{ top: coords.top, left: coords.left }}>
          {message}
        </MessageCard>
      </Portal>
    </Wrapper>
  )
}
